import React, { useState, useEffect, useRef } from "react";
import { Stage, Layer, Image as KonvaImage, Rect } from "react-konva";
import { readPsd, writePsd } from "ag-psd";
import useImage from "use-image";
import Konva from "konva";
import Photopea from "photopea";
import { Photo } from "@mui/icons-material";

const ImageLayer = ({ layer }) => {
  const [image] = useImage(layer.image || "");
  const [perspectiveImage, setPerspectiveImage] = useState(null);

  useEffect(() => {
    if (layer.perspectiveMatrix && image) {
      // Apply any additional transformations if needed
      setPerspectiveImage(image);
    } else {
      setPerspectiveImage(image);
    }
  }, [layer.perspectiveMatrix, image]);

  const compositeOperation =
    layer?.blendMode?.toLowerCase() !== "soft light"
      ? layer?.blendMode
      : "overlay";

  //   if (!perspectiveImage || layer.name === "Background Mask") return null;
  //  if(layer.name === "Shadows") {
  //   return null
  //   }

  //   if(layer.name === "Highlights") {
  //     return null
  //   }

  return (
    <KonvaImage
      image={perspectiveImage}
      width={layer.width}
      height={layer.height}
      globalCompositeOperation={compositeOperation}
      x={layer.x}
      y={layer.y}
    />
  );
};

const WhiteLayer = ({ width, height }) => (
  <Rect width={width} height={height} fill="white" />
);

export default function PsdOpen() {
  const [dots, setDots] = useState([
    { x: 50, y: 100 },
    { x: 700, y: 100 },
    { x: 700, y: 500 },
    { x: 50, y: 500 },
  ]);
  const [file, setFile] = useState(null);
  const [layers, setLayers] = useState([]);
  const [psdWidth, setPsdWidth] = useState(null);
  const [psdHeight, setPsdHeight] = useState(null);
  const [scaleRatio, setScaleRatio] = useState(1);
  const [psdData, setPsdData] = useState(null);
  const [stageDimensions, setStageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const stageRef = useRef(null);
  const [peaInstance, setPeaInstance] = useState(null);

  const meshPoints = [
    {
      x: 2586.5319832364057,
      y: 1646.100270728767,
    },
    {
      x: 2676.8818689745867,
      y: 2596.808879428368,
    },
    {
      x: 3388.1149664076565,
      y: 2738.3865350024403,
    },
    {
      x: 4200,
      y: 2625,
    },
    {
      x: 2589.334025877823,
      y: 2148.1029772511847,
    },
    {
      x: 2600,
      y: 2875,
    },
    {
      x: 3400,
      y: 2875,
    },
    {
      x: 4200,
      y: 2875,
    },
    {
      x: 2667.89649090281,
      y: 2445.805919902048,
    },
    {
      x: 2600,
      y: 3125,
    },
    {
      x: 3400,
      y: 3125,
    },
    {
      x: 4200,
      y: 3125,
    },
    {
      x: 2609.9948121006946,
      y: 2978.4533101094985,
    },
    {
      x: 2743.2365204607213,
      y: 3031.7318778416147,
    },
    {
      x: 3341.253266469168,
      y: 3611.655483610879,
    },
    {
      x: 4200,
      y: 3375,
    },
  ];

  const scaledMeshPoints = meshPoints.map((point) => ({
    x: point.x * scaleRatio, // Apply the same ratio used for layers
    y: point.y * scaleRatio,
  }));

  const calculateScaleRatio = (originalWidth) => {
    const targetWidth = 1100;
    return targetWidth / originalWidth;
  };

  const processPsdBuffer = (buffer) => {
    const psd = readPsd(buffer);

    setPsdData(psd);
    setPsdWidth(psd.width);
    setPsdHeight(psd.height);

    const ratio = calculateScaleRatio(psd.width);
    setScaleRatio(ratio);
    setStageDimensions({
      width: Math.floor(psd.width * ratio),
      height: Math.floor(psd.height * ratio),
    });

    const extractedLayers = psd.children
      .map((layer, i) => {
        if (layer.canvas) {
          let perspectiveMatrix = null;
          let dots;
          if (layer?.placedLayer) {
            if (layer?.placedLayer?.nonAffineTransform) {
              const [x1, y1, x2, y2, x3, y3, x4, y4] =
                layer.placedLayer.nonAffineTransform;
              perspectiveMatrix = [
                [x1 * ratio, y1 * ratio],
                [x2 * ratio, y2 * ratio],
                [x3 * ratio, y3 * ratio],
                [x4 * ratio, y4 * ratio],
              ];

              dots = [
                { x: x1 * ratio, y: y1 * ratio },
                { x: x2 * ratio, y: y2 * ratio },
                { x: x3 * ratio, y: y3 * ratio },
                { x: x4 * ratio, y: y4 * ratio },
              ];
            } else {
              const [x1, y1, x2, y2, x3, y3, x4, y4] =
                layer?.placedLayer.transform;
              perspectiveMatrix = [
                [x1 * ratio, y1 * ratio],
                [x2 * ratio, y2 * ratio],
                [x3 * ratio, y3 * ratio],
                [x4 * ratio, y4 * ratio],
              ];

              dots = [
                { x: x1 * ratio, y: y1 * ratio },
                { x: x2 * ratio, y: y2 * ratio },
                { x: x3 * ratio, y: y3 * ratio },
                { x: x4 * ratio, y: y4 * ratio },
              ];
            }

            setDots(dots);
          }

          const scaledCanvas = document.createElement("canvas");
          const ctx = scaledCanvas.getContext("2d");
          scaledCanvas.width = Math.floor(layer.canvas.width * ratio);
          scaledCanvas.height = Math.floor(layer.canvas.height * ratio);

          ctx.drawImage(
            layer.canvas,
            0,
            0,
            layer.canvas.width,
            layer.canvas.height,
            0,
            0,
            scaledCanvas.width,
            scaledCanvas.height
          );

          return {
            name: layer.name || `layer-${i}`,
            blendMode: layer.blendMode,
            image: scaledCanvas.toDataURL(),
            width: scaledCanvas.width,
            height: scaledCanvas.height,
            x: Math.floor(layer.left * ratio),
            y: Math.floor(layer.top * ratio),
            perspectiveMatrix,
          };
        }
        return null;
      })
      .filter(Boolean);

    setLayers(extractedLayers);
  };

  useEffect(() => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function (ev) {
      processPsdBuffer(ev.target.result);
    };
    reader.readAsArrayBuffer(file);
  }, [file]);

  function generateUUID() {
    // Simple UUID generator
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  const addNewLinkedFile = (fileData, fileName) => {
    const newLinkedFile = {
      id: generateUUID(), // Unique ID for the linked file
      name: fileName,
      data: fileData,

      // You can include other necessary properties here
    };

    // Add the new linked file to psdData
    const updatedPsdData = { ...psdData };
    updatedPsdData.linkedFiles = updatedPsdData.linkedFiles || [];
    updatedPsdData.linkedFiles.push(newLinkedFile);

    // Update the layer to reference this new linked file
    updateDesignLayerWithLinkedFile(updatedPsdData, newLinkedFile);

    // Update the state with the modified psdData
    setPsdData(updatedPsdData);

    // Console log the updated psdData

    // const outputBuffer = writePsd(updatedPsdData);
  };

  const updateDesignLayerWithLinkedFile = (psdData, linkedFile) => {
    // Find the design layer that needs to be updated
    const designLayer = findDesignLayer(psdData);

    if (designLayer) {
      // Update the 'smartObject' property

      // Update the 'placedLayer' property if necessary

      designLayer.placedLayer.id = linkedFile.id;

      designLayer.canvas = undefined;

      //   // Create a new canvas and draw the image onto it
      // const canvas = document.createElement('canvas');
      // const ctx = canvas.getContext('2d');

      // canvas.width = designLayer.canvas.width;
      // canvas.height = designLayer.canvas.height;

      // const img = new Image();
      // img.src = URL.createObjectURL(new Blob([linkedFile.data], { type: 'application/octet-stream' }));
      // img.onload = () => {
      //   ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      //   // Update the 'canvas' property with the new image
      //   designLayer.canvas = canvas;
      // };

      // If there are mesh points or transformations, ensure they are preserved
      // No changes are needed if you're just replacing the linked file

      // No need to update the psdData here as it's passed by reference
    } else {
      console.error("Design layer not found.");
    }
  };

  useEffect(() => {
    const container = document.getElementById("photopea-container");
    if (container) {
      let uri = "https://fastly.picsum.photos/id/10/2500/1667.jpg";
      Photopea.createEmbed(container, uri).then(async (pea) => {
        // photopea initialized
        // pea is the new Photopea object
        // you can also use async/await:
        setPeaInstance(pea);

        // load a file from a URL
        // await pea.openFromURL("https://fastly.picsum.photos/id/10/2500/1667.jpg?hmac=J04WWC_ebchx3WwzbM-Z4_KC_LeLBWr5LZMaAkWkF68", false);

        // Add event listener for file input change
        // document.getElementById('file-input').addEventListener('change', async (event) => {
        //     const file = event.target.files[0];
        //     if (file) {
        //         const arrayBuffer = await file.arrayBuffer();
        //         await pea.loadAsset(arrayBuffer);
        //     }
        // });

        // Add event listener for run script button
        // document.getElementById('run-script-button').addEventListener('click', async () => {
        //     const script = 'app.activeDocument.resizeImage(500, 500);';
        //     const result = await pea.runScript(script);
        // });

        // Add event listener for download PSD button
        // document.getElementById('download-psd-button').addEventListener('click', async () => {
        //     const downloadScript = 'app.activeDocument.saveToOE("psd:true")';
        //     const buffer = await pea.runScript(downloadScript);

        //     // const blob = new Blob([buffer], { type: 'application/octet-stream' });
        //     // const url = URL.createObjectURL(blob);
        //     // const a = document.createElement('a');
        //     // a.href = url;
        //     // a.download = 'downloaded.psd';
        //     // document.body.appendChild(a);
        //     // a.click();
        //     // document.body.removeChild(a);
        // });
      });
    }
  }, []);

  const saveUpdatedPsd = () => {
    if (!psdData) return;

    const updatedPsdBuffer = writePsd(psdData);

    const loadAsset = async (updatedPsd) => {
      const arrayBuffer = updatedPsd;

      if (!arrayBuffer) {
        throw new Error("ArrayBuffer is null or undefined");
      }

      await peaInstance.loadAsset(arrayBuffer);

      // Run a script to resize the image

      // const resizeResponse = await peaInstance.runScript(`app.activeDocument.layers[3].smartObject.resize(20,20);`);

      const PhotopeaResponse = await peaInstance.runScript(
        `app.activeDocument.saveToOE("psd")`
      );

      const latestPsd = PhotopeaResponse[0];

      processPsdBuffer(latestPsd);

      // const updatedPsdBuffer = readPsd(latestPsd);

      // const finalImage = await peaInstance.exportImage();
      //         const img = new Image();
      //         img.src = URL.createObjectURL(finalImage);
      //         document.body.appendChild(img);

      // save the updated psd
      // const blob = new Blob([latestPsd], { type: 'application/octet-stream' });
      // const url = URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = 'updated_design.psd';
      // a.click();
      // URL.revokeObjectURL(url);
    };

    if (updatedPsdBuffer) {
      loadAsset(updatedPsdBuffer);
    }

    // Save the updated PSD file
    // const blob = new Blob([updatedPsdBuffer], { type: 'application/octet-stream' });
    // const url = URL.createObjectURL(blob);
    // const a = document.createElement('a');
    // a.href = url;
    // a.download = 'updated_design.psd';
    // a.click();
    // URL.revokeObjectURL(url);
  };

  const findDesignLayer = (psdData) => {
    // Implement logic to find the specific design layer
    // This example searches by layer name
    let foundLayer = null;

    const searchLayers = (layers) => {
      for (const layer of layers) {
        if (layer.name === "Design") {
          foundLayer = layer;
          break;
        } else if (layer.children) {
          searchLayers(layer.children);
        }
      }
    };

    searchLayers(psdData.children);
    return foundLayer;
  };

  const handleDesignImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !psdData) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target.result;
      const uint8Array = new Uint8Array(arrayBuffer);

      // Proceed to create a new linked file
      addNewLinkedFile(uint8Array, file.name);
    };
    reader.readAsArrayBuffer(file);
  };

  // const handleDesignImageUpload = (e) => {
  //   const uploadedFile = e.target.files[0];
  //   if (!uploadedFile) return;

  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     const imageSrc = reader.result;

  //     const designLayer = layers.find((layer) => layer.name === "Design");
  //     if (!designLayer) {
  //       console.error("Design layer not found.");
  //       return;
  //     }

  //     // Create a canvas to apply perspective transformation
  //     const tempCanvas = document.createElement("canvas");
  //     tempCanvas.width = stageDimensions.width;
  //     tempCanvas.height = stageDimensions.height;
  //     const ctx = tempCanvas.getContext("2d");

  //     const img = new Image();
  //     img.src = imageSrc;
  //     img.onload = () => {
  //       if (designLayer.perspectiveMatrix) {
  //         const perspective = new Perspective(ctx, img);

  //         // Define destination points (corners of the canvas)
  //         const destPoints = [
  //           [0, 0],
  //           [tempCanvas.width, 0],
  //           [tempCanvas.width, tempCanvas.height],
  //           [0, tempCanvas.height],
  //         ];

  //         // Apply the perspective transformation
  //         // perspective.draw(designLayer.perspectiveMatrix, destPoints);
  //         perspective.draw([
  //           [dots[0].x, dots[0].y],
  //           [dots[1].x, dots[1].y],
  //           [dots[2].x, dots[2].y],
  //           [dots[3].x, dots[3].y],
  //         ] , destPoints);
  //       } else {
  //         ctx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);
  //       }

  //       const transformedImageSrc = tempCanvas.toDataURL();

  //       const newDesignLayer = {
  //         id: `design-image-${Date.now()}`,
  //         name: "Design Image",
  //         blendMode: "normal",
  //         image: transformedImageSrc,
  //         width: stageDimensions.width,
  //         height: stageDimensions.height,
  //         x: 0,
  //         y: 0,
  //       };

  //       const updatedLayers = layers.reduce((acc, layer) => {
  //         acc.push(layer);
  //         if (layer.name === "Design") {
  //           acc.push(newDesignLayer);
  //         }
  //         return acc;
  //       }, []);

  //       setLayers(updatedLayers);
  //     };
  //   };
  //   reader.readAsDataURL(uploadedFile);
  // };

  return (
    <div className="p-6 m-4 text-center">
      <input
        type="file"
        onChange={(e) => setFile(e.target.files[0])}
        accept=".psd"
      />
      <input type="file" onChange={handleDesignImageUpload} accept="image/*" />
      <button onClick={saveUpdatedPsd}>Save Updated PSD</button>

      <Stage
        ref={stageRef}
        width={stageDimensions.width}
        height={stageDimensions.height}
      >
        <Layer>
          {layers.map((layer, index) => (
            <ImageLayer key={index} layer={layer} />
          ))}
        </Layer>
      </Stage>

      <div style={{ width: "1500px", height: "700px", display: "none" }}>
        <div id="photopea-container" className="w-100 h-full"></div>
      </div>

      <div className="mt-4">
        <h3>Layers</h3>
        <ul>
          {layers.map((layer, index) => (
            <div key={index}>
              <li key={index}>{layer.name}</li>
              <img src={layer.image} />
            </div>
          ))}
        </ul>
      </div>
    </div>
  );
}
